import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

interface DeleteConfirmDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Handler for close/cancel */
  onClose: () => void;
  /** Handler for confirm delete */
  onConfirm: () => void;
  /** Title of the dialog */
  title?: string;
  /** Item name or description to show in confirmation message */
  itemName?: string;
  /** Custom confirmation message */
  message?: string;
  /** Confirm button text */
  confirmText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Whether the delete operation is in progress */
  isDeleting?: boolean;
}

/**
 * Reusable delete confirmation dialog
 *
 * Provides consistent UX for delete operations across the application.
 * Shows a warning message and requires explicit confirmation.
 *
 * @example
 * // Basic usage
 * const [open, setOpen] = useState(false);
 * const [itemToDelete, setItemToDelete] = useState<Item | null>(null);
 *
 * <DeleteConfirmDialog
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   onConfirm={handleDelete}
 *   itemName={itemToDelete?.name}
 * />
 *
 * @example
 * // Custom message
 * <DeleteConfirmDialog
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   onConfirm={handleDelete}
 *   title="Remove User"
 *   message="Are you sure you want to remove this user from the workspace?"
 * />
 */
export const DeleteConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Delete Confirmation',
  itemName,
  message,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  isDeleting = false,
}: DeleteConfirmDialogProps) => {
  const defaultMessage = itemName
    ? `Are you sure you want to delete "${itemName}"? This action cannot be undone.`
    : 'Are you sure you want to delete this item? This action cannot be undone.';

  // Prevent closing on backdrop click or escape key
  const handleClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
      disableEscapeKeyDown
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message || defaultMessage}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isDeleting}>
          {cancelText}
        </Button>
        <Button onClick={onConfirm} color="error" variant="contained" disabled={isDeleting}>
          {isDeleting ? 'Deleting...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
